type Listener = (token: string | null) => void;

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
  isRefreshable(): boolean;
  setRefreshable(value: boolean): void;
  clear(): void;
  subscribe(listener: Listener): () => void;
}

/**
 * An access token held in memory only.
 *
 * Not localStorage, not a readable cookie: anything JavaScript can read, an XSS
 * can exfiltrate. The long-lived refresh token is an httpOnly cookie the browser
 * attaches to one path and nothing else, so a page reload recovers the session
 * without ever exposing a durable credential to script.
 */
function createTokenStore(): TokenStore {
  let accessToken: string | null = null;
  /**
   * Whether a 401 may be answered by silently refreshing.
   *
   * False while a support session is in force. The refresh cookie belongs to the
   * *staff* account, so refreshing mid-impersonation would quietly hand back a
   * different identity and retry the merchant's request as somebody else - which
   * would be both confusing and wrong. A 401 during impersonation means the
   * session ended, and the caller should be told so.
   */
  let refreshable = true;
  const listeners = new Set<Listener>();

  const store: TokenStore = {
    get: () => accessToken,

    set(token) {
      accessToken = token;
      listeners.forEach((listener) => listener(token));
    },

    isRefreshable: () => refreshable,

    setRefreshable(value) {
      refreshable = value;
    },

    clear() {
      store.set(null);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  return store;
}

/** The merchant dashboard's session. */
export const tokenStore = createTokenStore();

/**
 * A shopper's session on a storefront.
 *
 * Genuinely separate state rather than a second key in one store: both can be
 * live at once (a merchant previewing their own shop is signed in twice, as two
 * different people), and a single slot would mean whichever surface loaded last
 * silently signed the other one out.
 */
export const customerTokenStore = createTokenStore();
